// src/utils/sanitizeHtml.js
//
// Primitivas de saneamento para conteúdo que vem de fora e acaba num v-html
// ou num :href.
//
// A regra que vale em todo lugar: ESCAPAR PRIMEIRO, formatar depois. Escapar
// só no fim, sobre uma string em que já se injetou HTML, desmancharia a própria
// formatação — e escapar "só o que parece perigoso" nunca fecha o buraco.
//
// Quem já usa esse padrão: o `safeMessage` do GlobalConfirmDialog (com sua
// própria cópia da lógica, por ser anterior a este util) e o
// `prescriptionSanitize.js`, que precisa de um whitelist de tags e por isso vai
// além destas primitivas.
//
// O whitelist de HTML rico é delegado ao DOMPurify. Escrever isso à mão é
// possível, mas a classe de falha que assombra sanitizadores caseiros é o mXSS:
// a saída parece limpa e volta a ser executável quando o NAVEGADOR a re-parseia,
// por diferenças de parsing em conteúdo estrangeiro (svg/math) e em elementos
// de texto cru. Manter isso em dia é um trabalho contínuo, e o DOMPurify existe
// justamente para carregá-lo.

import DOMPurify from "dompurify";

/**
 * Escapa os cinco caracteres com significado em HTML.
 * O `&` PRECISA vir primeiro, senão as entidades geradas depois seriam
 * escapadas de novo (`&lt;` viraria `&amp;lt;`).
 *
 * @param {*} value
 * @returns {string}
 */
export function escapeHtml(value) {
  if (value == null) return "";
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// `javascript:` e `data:` em href executam script no clique — só liberamos
// esquemas que não têm como executar nada.
const SAFE_URL_SCHEMES = new Set(["http:", "https:", "mailto:", "tel:"]);

/**
 * Devolve a URL quando ela usa um esquema seguro; caso contrário `null`.
 *
 * Use em qualquer `:href`/`:src` cujo valor venha do banco ou de integração
 * externa. Relativas são resolvidas contra a origem atual, então continuam
 * valendo. O caller decide o que fazer com o `null` (normalmente: não renderizar
 * o link).
 *
 * @param {*} url
 * @returns {string|null}
 */
export function safeUrl(url) {
  if (!url || typeof url !== "string") return null;

  const base =
    typeof window !== "undefined" && window.location
      ? window.location.origin
      : "http://localhost";

  let parsed;
  try {
    // O construtor já tira espaços em volta, o que impede disfarces do tipo
    // "  javascript:...". Se nem parsear, não é URL que valha a pena abrir.
    parsed = new URL(url, base);
  } catch {
    return null;
  }

  return SAFE_URL_SCHEMES.has(parsed.protocol) ? url : null;
}

// ────────────────────────────────────────────────────────────────────────────
// HTML rico (quando a formatação PRECISA sobreviver)
//
// `escapeHtml` serve para texto que só deve aparecer como texto. Quando o
// conteúdo é legitimamente formatado — um resumo com títulos e parágrafos, uma
// prescrição com blocos — escapar destruiria o documento. Aí a saída é passar
// por um whitelist: mantém as tags de formatação, joga fora todo o resto.
// ────────────────────────────────────────────────────────────────────────────

/** Tags de formatação sem capacidade de executar nada nem carregar recurso. */
export const FORMATTING_TAGS = new Set([
  "DIV", "SPAN", "P", "BR", "HR",
  "STRONG", "B", "EM", "I", "U", "S", "SMALL", "SUP", "SUB",
  "UL", "OL", "LI",
  "H1", "H2", "H3", "H4", "H5", "H6",
  "TABLE", "THEAD", "TBODY", "TFOOT", "TR", "TD", "TH",
]);

/**
 * O editor de prescrição embute um botão "×" em cada item. Só remover a tag
 * deixaria o "×" solto no meio do texto, porque com KEEP_CONTENT o DOMPurify
 * sobe os filhos do que apaga.
 *
 * O caminho óbvio seria passar `FORBID_CONTENTS: ['button']` na config — mas
 * essa opção SUBSTITUI a lista padrão inteira em vez de estendê-la, e a padrão
 * é o que descarta o miolo de script, style, svg, math e companhia. Copiá-la
 * para cá jogaria fora exatamente a manutenção que motivou adotar o DOMPurify:
 * a lista cresce a cada nova técnica descoberta. O hook remove só o botão e
 * deixa o resto da configuração intacto.
 */
const dropButtonSubtree = (node) => {
  if (node.nodeName === "BUTTON") node.remove();
};

/**
 * Devolve o HTML com apenas as tags do whitelist e SEM atributo algum (exceto
 * as classes explicitamente liberadas).
 *
 * Zerar os atributos é o que fecha `onerror`/`onclick`/`style`/`href` de uma
 * vez, em vez de tentar adivinhar quais são perigosos. Tag fora do whitelist é
 * "desembrulhada": some a tag, fica o texto — assim nenhum conteúdo legítimo é
 * perdido só por vir marcado de um jeito inesperado.
 *
 * O DOMPurify não tem como filtrar o VALOR do atributo class, só permitir o
 * atributo ou não. Por isso, quando há classes liberadas, um hook peneira a
 * lista depois que ele termina. O hook é registrado e removido em volta da
 * chamada: como JS é single-threaded e `sanitize` é síncrono, não há risco de
 * uma configuração vazar para outra.
 *
 * @param {string} html
 * @param {Object} [options]
 * @param {Set<string>} [options.allowedTags]    tags mantidas (maiúsculas)
 * @param {Set<string>} [options.allowedClasses] classes mantidas no atributo class
 * @returns {string}
 */
export function sanitizeRichHtml(html, options = {}) {
  if (!html || typeof html !== "string") return "";

  const allowedTags = options.allowedTags || FORMATTING_TAGS;
  const allowedClasses = options.allowedClasses || new Set();
  const keepsClasses = allowedClasses.size > 0;

  const config = {
    ALLOWED_TAGS: Array.from(allowedTags, (tag) => tag.toLowerCase()),
    ALLOWED_ATTR: keepsClasses ? ["class"] : [],
    ALLOW_DATA_ATTR: false,
    ALLOW_ARIA_ATTR: false,
    KEEP_CONTENT: true, // tag desconhecida some, texto permanece
  };

  const filterClasses = (node) => {
    if (!node.getAttribute || !node.hasAttribute?.("class")) return;
    const kept = node
      .getAttribute("class")
      .split(/\s+/)
      .filter((cls) => allowedClasses.has(cls));
    if (kept.length) node.setAttribute("class", kept.join(" "));
    else node.removeAttribute("class");
  };

  DOMPurify.addHook("uponSanitizeElement", dropButtonSubtree);
  if (keepsClasses) DOMPurify.addHook("afterSanitizeAttributes", filterClasses);
  try {
    return DOMPurify.sanitize(html, config);
  } finally {
    DOMPurify.removeHook("uponSanitizeElement");
    if (keepsClasses) DOMPurify.removeHook("afterSanitizeAttributes");
  }
}

/**
 * Extrai o texto de um HTML sem nunca construí-lo na página.
 *
 * Existe para substituir o idioma `div.innerHTML = html; div.textContent` —
 * que parece inofensivo por a div estar solta, mas basta um
 * `<img src=x onerror=...>` para o handler disparar, já que o navegador começa
 * a carregar o recurso independentemente de o nó estar no documento.
 *
 * @param {string} html
 * @returns {string}
 */
export function htmlToPlainText(html) {
  if (!html || typeof html !== "string") return "";
  const doc = new DOMParser().parseFromString(String(html), "text/html");
  return doc.body?.textContent || "";
}
